import MuiIcon from 'components/MuiIcon/MuiIcon';
import { Autocomplete, TextField, Typography } from '@mui/material';
import { FieldTemplate, FormTemplateType } from 'api/models';
import { useTranslation } from 'react-i18next';
import { Condition, FieldType } from 'api/models/FieldTemplate';
import { Box, styled, lighten, darken } from '@mui/system';
import { getFieldIcon } from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/AddFieldButton';
import { isUUID } from 'utils';

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor:
    theme.palette.mode === 'light'
      ? lighten(theme.palette.primary.light, 0.85)
      : darken(theme.palette.primary.main, 0.8),
}));

const GroupItems = styled('ul')({
  padding: 0,
});

function MenuItem({
  option,
  sxProps,
}: {
  option?: FieldTemplate;
  sxProps: any;
}) {
  const { t } = useTranslation();

  if (option?.alias?.includes('patient.')) {
    return (
      <Box component="li" {...sxProps}>
        <MuiIcon icon={getFieldIcon(option.fieldType)} sx={{ mr: 1 }} />
        {t(`projectCreation.availablePatientFields.${option?.id}`)}
      </Box>
    );
  }

  return (
    <Box
      component="li"
      {...sxProps}
      display="flex"
      flexDirection={'column'}
      justifyContent={'start'}
      alignItems={'flex-start'}
    >
      <Box width="100%">
        <Box display={'flex'}>
          <MuiIcon icon={getFieldIcon(option?.fieldType)} sx={{ mr: 1 }} />
          <Typography>{option?.fieldAttributes.label}</Typography>
        </Box>
        <Typography
          noWrap
          sx={{ color: theme => theme.palette.action.disabled }}
        >
          {option?.alias}
        </Typography>
      </Box>
    </Box>
  );
}

interface ConditionFieldAutocompleteProps {
  patientFields: FieldTemplate[];
  fieldTemplates: FieldTemplate[];
  formTemplateTypes: FormTemplateType[];
  condition?: Condition;
  onChange: (field: FieldTemplate | null) => void;
  disabled?: boolean;
  showMainCategories?: boolean;
}

export default function ConditionFieldAutocomplete({
  patientFields,
  fieldTemplates,
  formTemplateTypes,
  condition,
  onChange,
  disabled,
  showMainCategories,
}: ConditionFieldAutocompleteProps) {
  const { t } = useTranslation();
  // formTemplateType id and name map
  const formMap = new Map<string, string>();
  formTemplateTypes.forEach(formTemplateType => {
    formMap.set(formTemplateType.id, formTemplateType.name);
  });

  // group by formTemplateTypeId
  const groupedFieldTemplates = fieldTemplates
    // TODO: get rid of this once backend only returns supported fields with highest versions
    .filter(f => formMap.has(f.formTemplateTypeId ?? ''))
    .sort((a, b) => {
      if (a.formTemplateTypeId && b.formTemplateTypeId) {
        return a.formTemplateTypeId.localeCompare(b.formTemplateTypeId);
      }
      return 0;
    });

  const fields = [
    showMainCategories
      ? {
          id: 'divider',
          fieldAttributes: {
            label: 'patientFields',
          },
          fieldType: FieldType.Divider,
          type: '',
          alias: '',
        }
      : null,
    ...patientFields,
    showMainCategories
      ? {
          id: 'divider',
          fieldAttributes: {
            label: 'fields',
          },
          fieldType: FieldType.Divider,
          type: '',
          alias: '',
        }
      : null,
    ...groupedFieldTemplates,
  ].filter(Boolean) as FieldTemplate[];

  const value =
    fields.find(f =>
      isUUID(f.id)
        ? f.alias === condition?.reference.alias
        : f.id === condition?.reference.attribute,
    ) ?? null;

  return (
    <Autocomplete
      disabled={disabled}
      options={fields}
      onChange={(_, value) => {
        if (value?.id === 'divider') return;
        onChange(value);
      }}
      renderGroup={params => (
        <li key={params.key}>
          <GroupHeader>{params.group}</GroupHeader>
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
      groupBy={option => {
        if (option.id === 'divider')
          return t(
            `dialogs.addField.fieldAutocomplete.${option.fieldAttributes.label}`,
          );

        if (option.formTemplateTypeId) {
          const name = formMap.get(option.formTemplateTypeId);
          if (name) {
            return name;
          }
          if (!name && isUUID(option.id)) {
            return t(`dialogs.addField.fieldAutocomplete.missingFormTemplate`);
          }
        }
        return '';
      }}
      getOptionLabel={option => {
        if (option.id === 'divider') return '';
        return isUUID(option.id)
          ? option.fieldAttributes.label ?? ''
          : t(`projectCreation.availablePatientFields.${option.id}`);
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={`${t('general.field')} *`}
          error={!value}
          helperText={!value && t('general.errors.required')}
        />
      )}
      renderOption={(props, option) => {
        if (option.id === 'divider') return null;
        return (
          <MenuItem
            option={option as FieldTemplate}
            sxProps={props}
            key={option.id}
          />
        );
      }}
      value={value}
    />
  );
}
